// Screen 5 · Fast-capture — the type-it path (core of Module D).
// Frame + the three hard cells (friendly prompts, NO red asterisks) + body.
// Save draft or Submit → render the 5-stage plan. No quality meter, no scoring.
//
// NOTE: this is a functional skeleton wired to the real API so the loop runs end-to-end.
// Claude Design owns the final visual treatment (Design Brief screen 5) — restyle, keep the contract.

import { api } from "../api";
import { esc, lockPaste } from "../dom";
import { navigate } from "../router";

interface Cls {
  id: string;
  name: string;
  level: string | null;
}
interface TermInfo {
  term: { id: string };
  weeks: number[];
  current_week: number;
}

export async function renderPlanNew(app: HTMLElement): Promise<void> {
  const [{ classes }, term] = await Promise.all([
    api<{ classes: Cls[] }>("/api/classes"),
    api<TermInfo>("/api/terms/current"),
  ]);

  if (classes.length === 0) {
    app.innerHTML = `
      <div class="topbar"><a data-link href="/this-week" class="muted">← This Week</a></div>
      <h1>Plan a lesson</h1>
      <p class="empty">Add a class first, then come back to plan a lesson.</p>
      <a class="btn" data-link href="/classes">Add a class</a>`;
    return;
  }

  const classOpts = classes
    .map((c) => `<option value="${esc(c.id)}">${esc(c.name)}${c.level ? ` · ${esc(c.level)}` : ""}</option>`)
    .join("");
  const weekOpts = term.weeks
    .map((w) => `<option value="${w}"${w === term.current_week ? " selected" : ""}>Week ${w}</option>`)
    .join("");

  app.innerHTML = `
    <div class="topbar"><a data-link href="/this-week" class="muted">← This Week</a></div>
    <h1>Plan a lesson</h1>

    <label for="class">Class</label>
    <select id="class">${classOpts}</select>

    <label for="week">Week</label>
    <select id="week">${weekOpts}</select>

    <label for="topic">Topic</label>
    <input id="topic" placeholder="Talking about the weather" required />

    <label for="duration">Duration (minutes)</label>
    <input id="duration" type="number" value="60" min="5" step="5" />

    <h2>The thinking</h2>
    <p class="hint">Type these in your own words — paste is off here so it stays your English.</p>
    <label for="prior">Prior knowledge (for this class)</label>
    <p class="hint">What did this class do last time that connects to this?</p>
    <textarea id="prior"></textarea>

    <label for="misconception">The predictable misconception</label>
    <p class="hint">What's the one thing they'll likely get wrong here?</p>
    <textarea id="misconception"></textarea>

    <label for="cfu">The check for understanding</label>
    <p class="hint">How will you <em>see</em> that they got it, before you move on?</p>
    <textarea id="cfu"></textarea>

    <h2>The plan</h2>
    <label for="materials">Objectives · Materials</label>
    <p class="hint">You can paste a vocab or materials list here.</p>
    <textarea id="materials"></textarea>

    <label for="procedure">Lesson procedure (Steps 1–5)</label>
    <p class="hint">Type the steps in your own words — paste is off here.</p>
    <textarea id="procedure" style="min-height:160px"></textarea>

    <div id="err" class="error" hidden></div>
    <div style="height:16px"></div>
    <button class="btn secondary" id="draft" type="button">Save draft</button>
    <div style="height:8px"></div>
    <button class="btn" id="submit" type="button">Submit</button>`;

  // Paste is locked on the thinking cells + procedure so the text is the teacher's own
  // English (spec §2, §4.4). Materials, topic, class and week stay pasteable.
  for (const id of ["#prior", "#misconception", "#cfu", "#procedure"]) {
    lockPaste(app.querySelector<HTMLElement>(id));
  }

  const val = (id: string): string => {
    const el = app.querySelector(id) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
    return el ? el.value : "";
  };
  const err = app.querySelector<HTMLElement>("#err")!;

  async function save(submit: boolean): Promise<void> {
    err.hidden = true;
    const topic = val("#topic").trim();
    if (!topic) {
      err.textContent = "Add a topic so you can find this lesson again.";
      err.hidden = false;
      return;
    }
    try {
      // 1) create the lesson frame
      const lesson = await api<{ id: string }>("/api/lessons", {
        method: "POST",
        body: JSON.stringify({
          class_id: val("#class"),
          term_id: term.term.id,
          week_no: Number(val("#week")),
          topic,
          duration_min: Number(val("#duration")) || 60,
        }),
      });
      // 2) save the three cells + materials/procedure
      const plan = await api<{ plan_id: string }>(`/api/lessons/${lesson.id}/plan`, {
        method: "PUT",
        body: JSON.stringify({
          prior: val("#prior"),
          misconception: val("#misconception"),
          check_for_understanding: val("#cfu"),
          materials: val("#materials"),
          procedure: val("#procedure"),
        }),
      });
      // 3) optionally submit
      if (submit) {
        await api(`/api/lessons/${lesson.id}/submit`, { method: "POST" });
      }
      navigate(`/plan/${plan.plan_id}`);
    } catch (ex) {
      err.textContent = (ex as Error).message;
      err.hidden = false;
    }
  }

  app.querySelector<HTMLButtonElement>("#draft")!.addEventListener("click", () => void save(false));
  app.querySelector<HTMLButtonElement>("#submit")!.addEventListener("click", () => void save(true));
}
